import { diContainer } from '@/coreContainer/container';
import {
  convertTargetFieldToConditionKey,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
  UnmanagedBusinessAutoRegisterSettingItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { FetchRequiredFilterType } from '@/coreDomainsModule/entities/filter';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import { ref } from 'vue';

/**
 * 任意項目候補Composableの引数
 */
type UseUnmanagedBusinessAutoRegisterOptionalItemOptionsOptions = {
  getSettings: () => UnmanagedBusinessAutoRegisterSettingItem[];
  getConditionValueOptions: (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem) => string[];
};

/**
 * 任意項目候補の生成と読み込みロジックを提供する
 * @param options - 候補生成に必要な依存関数
 */
export const useUnmanagedBusinessAutoRegisterOptionalItemOptions = (
  options: UseUnmanagedBusinessAutoRegisterOptionalItemOptionsOptions
) => {
  const { getSettings, getConditionValueOptions } = options;

  const forecastForReceiptMemoSuggestions = ref<string[]>([]);
  const optionalItemMasterOptions = ref<Partial<Record<UnmanagedBusinessAutoRegisterField, string[]>>>({});

  const optionalItemFilterTypeMap: Partial<Record<UnmanagedBusinessAutoRegisterField, FetchRequiredFilterType>> = {
    unit: 'UNIT',
    manufacturer: 'MANUFACTURER',
    endUser: 'END_USER',
    orderPackage: 'ORDER_PACKAGE',
    moq: 'MOQ',
    purchaseUnitPrice: 'PURCHASE_UNIT_PRICE',
    salesUnitPrice: 'SALES_UNIT_PRICE',
  };

  /**
   * 任意項目候補として表示可能な値か判定する
   * @param value - 候補値
   */
  const isDisplayableOptionalItemOption = (value: string): boolean => {
    return value !== '' && value !== UNASSIGNED_COLUMN;
  };

  /**
   * 受領用内示名の候補一覧を取得する
   */
  const loadForecastForReceiptMemoSuggestions = async (): Promise<void> => {
    const result = await diContainer.forecastForReceiptRepository.getForecastForReceipt();
    if (result.status.type !== 'success') {
      forecastForReceiptMemoSuggestions.value = [];
      return;
    }

    const forecastForReceiptNames = result.status.data.data
      .map((forecastForReceipt) => forecastForReceipt.forecastForReceiptName.trim())
      .filter((forecastForReceiptName) => forecastForReceiptName !== '');

    forecastForReceiptMemoSuggestions.value = [...new Set(forecastForReceiptNames)];
  };

  /**
   * 任意項目のマスタ候補一覧を取得する
   */
  const loadOptionalItemMasterSuggestions = async (): Promise<void> => {
    const entries = Object.entries(optionalItemFilterTypeMap) as Array<[UnmanagedBusinessAutoRegisterField, FetchRequiredFilterType]>;

    await Promise.all(
      entries.map(async ([targetField, filterType]) => {
        const result = await diContainer.getFilterInfoUseCase.Execute(
          filterType,
          '',
          'inventory',
          undefined,
          undefined,
          initPagingOptions(2000)
        );
        if (result.status.type !== 'success') {
          optionalItemMasterOptions.value[targetField] = [];
          return;
        }

        optionalItemMasterOptions.value[targetField] = [
          ...new Set(result.status.data.data.map((value) => String(value).trim()).filter(isDisplayableOptionalItemOption)),
        ];
      })
    );
  };

  /**
   * 任意項目候補の参照元を読み込む
   */
  const loadOptionalItemOptionSources = async (): Promise<void> => {
    await Promise.all([loadForecastForReceiptMemoSuggestions(), loadOptionalItemMasterSuggestions()]);
  };

  /**
   * 任意項目に紐づく候補一覧を返す
   * @param targetField - 任意項目の対象フィールド
   */
  const getOptionalItemBaseOptions = (targetField: UnmanagedBusinessAutoRegisterField): string[] => {
    const registeredOptions = getSettings()
      .flatMap((setting) => setting.registerContent.optionalItems)
      .filter((optionalItem) => optionalItem.targetField === targetField)
      .map((optionalItem) => optionalItem.targetValue.trim())
      .filter(isDisplayableOptionalItemOption);

    const masterOptions = optionalItemMasterOptions.value[targetField] ?? [];
    const forecastForReceiptOptions = targetField === 'forecastForReceiptMemo' ? forecastForReceiptMemoSuggestions.value : [];

    const availableConditionSuggestionFields: UnmanagedBusinessAutoRegisterField[] = [
      'supplierNumber',
      'supplierName',
      'businessNumber',
      'businessName',
      'warehouseNumber',
      'warehouse',
      'package',
      'customerNumber',
      'customerName',
      'salesDestinationNumber',
      'salesDestinationName',
    ];
    if (!availableConditionSuggestionFields.includes(targetField)) {
      return [...new Set([...registeredOptions, ...masterOptions, ...forecastForReceiptOptions])].filter(isDisplayableOptionalItemOption);
    }

    const conditionKey = convertTargetFieldToConditionKey(targetField);
    const conditionOptions = getConditionValueOptions({
      targetFieldType: conditionKey.targetFieldType,
      searchType: conditionKey.searchType,
      targetValue: '',
    });

    return [...new Set([...registeredOptions, ...masterOptions, ...forecastForReceiptOptions, ...conditionOptions])].filter(
      isDisplayableOptionalItemOption
    );
  };

  /**
   * 入力値に応じた任意項目候補を返す
   * @param targetField - 任意項目の対象フィールド
   * @param currentValue - 現在の入力値
   */
  const getOptionalItemOptions = (targetField: UnmanagedBusinessAutoRegisterField, currentValue: string): string[] => {
    const normalizedCurrentValue = currentValue.trim().toLocaleLowerCase();
    const baseOptions = getOptionalItemBaseOptions(targetField);
    if (normalizedCurrentValue === '') {
      return baseOptions;
    }

    return baseOptions.filter((optionValue) => {
      return optionValue.toLocaleLowerCase().includes(normalizedCurrentValue);
    });
  };

  return {
    loadOptionalItemOptionSources,
    getOptionalItemOptions,
  };
};
